import L, { type LatLng, type LatLngBounds } from "leaflet";

/**
 * 地図出力（印刷モード）のレイアウト計算ユーティリティ。
 *
 * 地図中心 + 用紙サイズ / 向き / 縮尺から、用紙上の地図領域 (mm) や
 * 緯度経度の出力範囲 (bounds) を算出する純粋関数を提供する。React や
 * Leaflet マップインスタンスには依存しないため、フックやコンポーネント、
 * ユニットテストから独立して利用できる。
 */

export type MapExportPaperSize = "a4" | "a3";
export type MapExportOrientation = "portrait" | "landscape";

export interface MapPrintOptions {
  paperSize: MapExportPaperSize;
  orientation: MapExportOrientation;
  scaleDenominator?: number;
  dpi: number;
}

export interface PrintLayout {
  pageWidthMm: number;
  pageHeightMm: number;
  marginMm: number;
  titleHeightMm: number;
  footerHeightMm: number;
  mapXmm: number;
  mapYmm: number;
  mapWidthMm: number;
  mapHeightMm: number;
}

const PAPER_SIZES_MM: Record<MapExportPaperSize, [number, number]> = {
  a4: [210, 297],
  a3: [297, 420],
};

export const PRINT_MARGIN_MM = 12;
export const PRINT_TITLE_HEIGHT_MM = 14;
export const PRINT_FOOTER_HEIGHT_MM = 10;

/**
 * DPIから1mmあたりのピクセル数を算出する。
 */
export const getPxPerMm = (dpi: number): number => dpi / 25.4;

/**
 * 地図中心とメートル単位の幅・高さから LatLngBounds を生成する。
 * 緯度経度の度数換算には WGS84 の赤道半径 (6378137m) を用いる近似式を採用。
 */
export const createBoundsFromCenterMeters = (
  center: LatLng,
  widthMeters: number,
  heightMeters: number
): LatLngBounds => {
  const earthRadiusMeters = 6378137;
  const halfHeight = heightMeters / 2;
  const halfWidth = widthMeters / 2;
  const latOffset = (halfHeight / earthRadiusMeters) * (180 / Math.PI);
  const lngOffset =
    (halfWidth / (earthRadiusMeters * Math.cos((center.lat * Math.PI) / 180))) *
    (180 / Math.PI);

  return L.latLngBounds(
    [center.lat - latOffset, center.lng - lngOffset],
    [center.lat + latOffset, center.lng + lngOffset]
  );
};

/**
 * 用紙サイズと向きから、印刷出力時の地図領域（タイトル・フッターを除いた
 * 中央のキャプチャ範囲）を mm 単位で算出する。
 */
export const getPrintLayout = ({
  paperSize,
  orientation,
}: MapPrintOptions): PrintLayout => {
  const [shortSide, longSide] = PAPER_SIZES_MM[paperSize];
  const pageWidthMm = orientation === "portrait" ? shortSide : longSide;
  const pageHeightMm = orientation === "portrait" ? longSide : shortSide;
  const mapXmm = PRINT_MARGIN_MM;
  const mapYmm = PRINT_MARGIN_MM + PRINT_TITLE_HEIGHT_MM;
  const mapWidthMm = pageWidthMm - PRINT_MARGIN_MM * 2;
  const mapHeightMm =
    pageHeightMm -
    PRINT_MARGIN_MM * 2 -
    PRINT_TITLE_HEIGHT_MM -
    PRINT_FOOTER_HEIGHT_MM;

  return {
    pageWidthMm,
    pageHeightMm,
    marginMm: PRINT_MARGIN_MM,
    titleHeightMm: PRINT_TITLE_HEIGHT_MM,
    footerHeightMm: PRINT_FOOTER_HEIGHT_MM,
    mapXmm,
    mapYmm,
    mapWidthMm,
    mapHeightMm,
  };
};
